/**
 * Step 2 — Child Profile (Phase 2 task #3).
 *
 * Per plan §6.1 task #3:
 *  - Nama input (1-20 char, required, validated)
 *  - Usia picker (2-6, default 3 tahun, label PG/TK A/TK B)
 *  - CTA "Lanjut" disabled until name valid
 *  - Wire to API: POST /v1/child on submit
 *
 * Design notes:
 *  - Age picker is a 5-chip segmented control rather than a continuous
 *    slider. 5 discrete values map 1:1 to age brackets; chips make
 *    "what's available" obvious and avoid the slider's snap-to-tick
 *    question. The class label (PG / TK A / TK B) updates as the user
 *    picks an age so the connection is visible.
 *  - Name input is the only field that gates submit. Usia is always
 *    valid (default 3) so there is no second gate to wire.
 *  - On submit, we POST /v1/child, store the returned ChildProfile in
 *    OnboardingContext, then push to /quiz-activity. Error path: show the
 *    server's message, keep the form mounted, allow retry.
 *
 * Server contract (apps/api/src/lib/validators.ts childCreateSchema):
 *  - name: string 1-20 (UU PDP limit)
 *  - age: int 2-6
 *  - classId: literal 1|2|3 (derived from age via age-class.ts)
 *  - avatarId: int 0-99, default 0
 *  - isDefault: bool, default true
 *  - Header X-Device-Id required (auto-injected by apiFetch)
 */

import { useMemo, useState } from 'react';
import { useRouter } from 'expo-router';
import { Pressable, ScrollView, StyleSheet, TextInput, View } from 'react-native';

import { ThemedText } from '@/components/themed-text';
import { OnboardingStepShell } from '@/components/onboarding/OnboardingStepShell';
import { TrialWall } from '@/components/trial-wall';
import { Brand, Radii, Spacing } from '@/constants/theme';
import { createChild, getActiveChild } from '@/lib/api/children';
import { ApiError } from '@/lib/api/client';
import { AGE_BRACKETS, classIdToLabel, ageToClassId } from '@/lib/age-class';
import { updateStoredDisplayName } from '@/lib/auth';
import { trackAnalyticsEvent } from '@/lib/analytics';
import { useOnboarding } from '@/contexts/onboarding-context';
import type { AgeBracket } from '@funpaper/shared';

import { ONBOARDING_ROUTES, getStepByPath } from '@/constants/onboarding';

const MIN_NAME_LEN = 1;
const MAX_NAME_LEN = 20;
const MAX_DISPLAY_NAME_LEN = 20;

export default function ChildProfileScreen() {
  const router = useRouter();
  const step = getStepByPath(ONBOARDING_ROUTES.childProfile);
  const { currentChild, setCurrentChild } = useOnboarding();

  const [displayName, setDisplayName] = useState('');
  const [name, setName] = useState('');
  const [age, setAge] = useState<AgeBracket>(3);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Plan 17: trial guest habis saat submit → tampilkan TrialWall (CTA login). */
  const [trialExpired, setTrialExpired] = useState(false);

  const trimmedName = name.trim();
  const nameLength = trimmedName.length;
  const isNameValid = nameLength >= MIN_NAME_LEN && nameLength <= MAX_NAME_LEN;
  const isNameTooLong = name.length > MAX_NAME_LEN;
  const displayNameLength = displayName.trim().length;
  const isDisplayNameTooLong = displayName.length > MAX_DISPLAY_NAME_LEN;
  const canSubmit = isNameValid && !isSubmitting;

  const classId = useMemo(() => ageToClassId(age), [age]);
  const classLabel = useMemo(() => classIdToLabel(classId), [classId]);

  const onKembali = () => router.back();
  const onLewati = () => {
    trackAnalyticsEvent('Onboarding Step Completed', { step_name: 'child_profile', skipped: true });
    router.replace(ONBOARDING_ROUTES.summary);
  };

  const onLanjut = async () => {
    if (!canSubmit) return;
    setIsSubmitting(true);
    setError(null);
    try {
      const existingChild = await getActiveChild();
      if (existingChild) {
        if (displayNameLength > 0) updateStoredDisplayName(displayName.trim());
        setCurrentChild(existingChild);
        router.replace('/home' as never);
        return;
      }
      const child = await createChild({ name: trimmedName, age });
      if (displayNameLength > 0) updateStoredDisplayName(displayName.trim());
      setCurrentChild(child);
      trackAnalyticsEvent('Onboarding Step Completed', { step_name: 'child_profile', skipped: false });
      router.push(ONBOARDING_ROUTES.quizActivity);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'TrialExpired') {
        // Trial habis: error box saja membuat user buntu — tampilkan
        // TrialWall yang membawa CTA "Masuk dengan Google" (pola home.tsx).
        setTrialExpired(true);
        setIsSubmitting(false);
        return;
      }
      const message =
        err instanceof ApiError
          ? err.message
          : 'Gagal menyimpan profil anak. Coba lagi.';
      setError(message);
      setIsSubmitting(false);
    }
  };

  return (
    <OnboardingStepShell
      progress={step?.progress ?? 2 / 5}
      stepLabel={step?.stepLabel ?? '2 / 5'}
      onKembali={onKembali}
      onLewati={currentChild ? onLewati : undefined}
      onLanjut={onLanjut}
      lanjutDisabled={!canSubmit}
      lanjutLabel={isSubmitting ? 'Menyimpan…' : 'Lanjut'}
    >
      <View style={styles.headerBlock}>
        <ThemedText type="subtitle" style={styles.title}>
          Kenalan dulu yuk
        </ThemedText>
        <ThemedText
          type="default"
          themeColor="textSecondary"
          style={styles.subtitle}
        >
          Boleh pakai nama panggilan, tidak harus nama asli. Data si kecil membantu kami memilih worksheet yang pas.
        </ThemedText>
      </View>

      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.form}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.field}>
          <ThemedText type="default" style={styles.label}>
            Nama panggilan kamu (opsional)
          </ThemedText>
          <TextInput
            value={displayName}
            onChangeText={(t) => setDisplayName(t)}
            placeholder="Misal: Kak Rani"
            placeholderTextColor="#9b96a4"
            maxLength={MAX_DISPLAY_NAME_LEN}
            style={styles.input}
            editable={!isSubmitting}
            autoCapitalize="words"
            autoCorrect={false}
            returnKeyType="next"
            accessibilityLabel="Nama panggilan kamu, opsional"
          />
          <View style={styles.helpRow}>
            <ThemedText
              type="small"
              themeColor={isDisplayNameTooLong ? 'danger' : 'textSecondary'}
              style={styles.helpText}
            >
              Kami pakai untuk menyapa kamu di aplikasi.
            </ThemedText>
            <ThemedText
              type="small"
              themeColor="textSecondary"
              style={styles.helpCounter}
            >
              {displayNameLength}/{MAX_DISPLAY_NAME_LEN}
            </ThemedText>
          </View>
        </View>

        <View style={styles.field}>
          <ThemedText type="default" style={styles.label}>
            Nama anak
          </ThemedText>
          <TextInput
            value={name}
            onChangeText={(t) => {
              setName(t);
              if (error) setError(null);
            }}
            placeholder="Misal: Aira"
            placeholderTextColor="#9b96a4"
            maxLength={MAX_NAME_LEN}
            style={styles.input}
            editable={!isSubmitting}
            autoCapitalize="words"
            autoCorrect={false}
            returnKeyType="next"
            accessibilityLabel="Nama anak"
          />
          <View style={styles.helpRow}>
            <ThemedText
              type="small"
              themeColor={isNameTooLong ? 'danger' : 'textSecondary'}
              style={styles.helpText}
            >
              {isNameTooLong
                ? `Maksimum ${MAX_NAME_LEN} karakter`
                : 'Bebas, hanya untuk sapaan di worksheet'}
            </ThemedText>
            <ThemedText
              type="small"
              themeColor={
                nameLength === 0
                  ? 'textSecondary'
                  : isNameValid
                  ? 'success'
                  : 'danger'
              }
              style={styles.helpCounter}
            >
              {nameLength}/{MAX_NAME_LEN}
            </ThemedText>
          </View>
        </View>

        <View style={styles.field}>
          <ThemedText type="default" style={styles.label}>
            Usia
          </ThemedText>
          <View style={styles.ageRow} accessibilityRole="radiogroup">
            {AGE_BRACKETS.map((a) => {
              const active = a === age;
              return (
                <Pressable
                  key={a}
                  onPress={() => !isSubmitting && setAge(a)}
                  disabled={isSubmitting}
                  style={({ pressed }) => [
                    styles.ageChip,
                    active && styles.ageChipActive,
                    pressed && !active && styles.ageChipPressed,
                  ]}
                  accessibilityRole="radio"
                  accessibilityState={{ selected: active, disabled: isSubmitting }}
                  accessibilityLabel={`Usia ${a} tahun`}
                >
                  <ThemedText
                    type="default"
                    style={[styles.ageText, active && styles.ageTextActive]}
                  >
                    {a}
                  </ThemedText>
                </Pressable>
              );
            })}
          </View>
          <ThemedText
            type="small"
            themeColor="textSecondary"
            style={styles.classLabel}
          >
            {age} tahun → {classLabel}
          </ThemedText>
        </View>

        {error ? (
          <View style={styles.errorBox} accessibilityRole="alert">
            <ThemedText type="small" style={styles.errorText}>
              {error}
            </ThemedText>
          </View>
        ) : null}

        {/* Trial habis saat submit — Modal overlay dengan CTA login Google. */}
        <TrialWall visible={trialExpired} />
      </ScrollView>
    </OnboardingStepShell>
  );
}

const CHIP_BG = '#ffffff';
const CHIP_BG_ACTIVE = Brand.primary;
const CHIP_BORDER = '#e5e5e5';
const CHIP_BORDER_ACTIVE = Brand.primary;

const styles = StyleSheet.create({
  headerBlock: {
    gap: Spacing.two,
    paddingTop: Spacing.three,
  },
  title: {
    color: Brand.primary,
  },
  subtitle: {
    lineHeight: 22,
  },
  scroll: {
    flex: 1,
  },
  form: {
    flexGrow: 1,
    paddingTop: Spacing.five,
    paddingBottom: Spacing.four,
    gap: Spacing.five,
  },
  field: {
    gap: Spacing.two,
  },
  label: {
    fontWeight: '600',
    fontSize: 15,
    color: '#201c23',
  },
  input: {
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: CHIP_BORDER,
    borderRadius: Radii.medium,
    paddingVertical: Spacing.three,
    paddingHorizontal: Spacing.three,
    fontSize: 16,
    color: '#201c23',
    minHeight: 48,
  },
  helpRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  helpText: {
    flex: 1,
  },
  helpCounter: {
    fontVariant: ['tabular-nums'],
    marginLeft: Spacing.two,
  },
  ageRow: {
    flexDirection: 'row',
    gap: Spacing.two,
  },
  ageChip: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: CHIP_BG,
    borderWidth: 1,
    borderColor: CHIP_BORDER,
    borderRadius: Radii.medium,
    paddingVertical: Spacing.three,
    minHeight: 52,
  },
  ageChipActive: {
    backgroundColor: CHIP_BG_ACTIVE,
    borderColor: CHIP_BORDER_ACTIVE,
  },
  ageChipPressed: {
    backgroundColor: 'rgba(86, 63, 122, 0.08)',
  },
  ageText: {
    fontSize: 18,
    fontWeight: '600',
    color: '#201c23',
  },
  ageTextActive: {
    color: '#ffffff',
  },
  classLabel: {
    marginTop: Spacing.one,
  },
  errorBox: {
    backgroundColor: 'rgba(229, 62, 62, 0.08)',
    borderColor: 'rgba(229, 62, 62, 0.3)',
    borderWidth: 1,
    borderRadius: Radii.medium,
    paddingVertical: Spacing.two,
    paddingHorizontal: Spacing.three,
  },
  errorText: {
    color: Brand.danger,
    fontWeight: '500',
  },
});
